//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from 'unison';

import { CatalogSelectionModalActions } from '@catalog-ingestion/actions/catalogSelectionModalActions';
import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import { GoogleCatalogIngestionModal } from '@catalog-ingestion/components/modals/google-catalog-ingestion-modal/GoogleCatalogIngestionModal';
import {
  CATALOG_INGESTION_TYPES,
  getCatalogTypes,
} from '@catalog-ingestion/constants/catalogSelectionConstants';
import RequiredPermissionsButton from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { ICuralateStore } from '@models/AppState';
import styled from 'styled-components';
import { ReduxDispatch } from 'app/curalate.store';

function mapStateToProps(state: ICuralateStore) {
  return {
    isOpen: state.catalogIngestionReducers.catalogSelection.isOpen,
    selectedCatalogType:
      state.catalogIngestionReducers.catalogSelection.selectedCatalogType,
    isGoogleCatalogIngestionModalOpen:
      state.catalogIngestionReducers.googleCatalogIngestionModal.isOpen,
    isMagentoCapabilitiesEnabled:
      state.session.features.magento_capabilities_enabled,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  return {
    actions: {
      catalogSelectionModal: bindActionCreators(
        CatalogSelectionModalActions,
        dispatch,
      ),
      googleCatalogIngestionModal: bindActionCreators(
        GoogleCatalogIngestionModalActions,
        dispatch,
      ),
    },
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
export const CatalogSelectionModal: React.FunctionComponent<Props> = (
  props,
) => {
  const {
    isOpen,
    selectedCatalogType,
    isMagentoCapabilitiesEnabled,
    actions: {
      catalogSelectionModal: {
        changeCatalogType,
        closeCatalogSelectionModal,
        setSelectedEcommerceType,
      },
      googleCatalogIngestionModal: { openGoogleCatalogIngestionModal },
    },
  } = props;

  const enableEcommerceFeatures = () => {
    switch (selectedCatalogType) {
      case CATALOG_INGESTION_TYPES.SHOPIFY:
        setSelectedEcommerceType();
        closeCatalogSelectionModal();
        break;
      case CATALOG_INGESTION_TYPES.MAGENTO:
        setSelectedEcommerceType();
        closeCatalogSelectionModal();
        break;
      case CATALOG_INGESTION_TYPES.OTHER:
        setSelectedEcommerceType();
        openGoogleCatalogIngestionModal();
        closeCatalogSelectionModal();
        break;
    }
  };

  let catalogTypes = getCatalogTypes();
  if (!isMagentoCapabilitiesEnabled) {
    catalogTypes = catalogTypes.filter(
      (entry) => entry.label !== CATALOG_INGESTION_TYPES.MAGENTO,
    );
  }

  return (
    <>
      <Modal isOpen={isOpen}>
        <ModalHeader className='cu-wide-measure cu-d-flex cu-align-items-center cu-flex-column'>
          <div className='cu-wide-measure cu-d-flex cu-align-items-center cu-flex-column'>
            <div className='cu-text-subtitle'>
              <strong>Welcome to Bazaarvoice!</strong>
            </div>
            <div className='cu-text-body'>
              Tell us a little about your brand
            </div>
          </div>
        </ModalHeader>
        <ModalBody className='cu-d-block cu-wide-measure'>
          <div>
            <strong>What online store platform do you use?</strong>
          </div>
          <div>
            <Select
              className='cu-mt-2'
              handleChange={changeCatalogType}
              unselectedLabel={'Select a platform'}
              value={selectedCatalogType}
              items={catalogTypes}
            >
              {catalogTypes.map(({ label, value }) => (
                <SelectItem
                  className='cu-mt-2'
                  key={label}
                  label={label}
                  value={value}
                />
              ))}
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className='cu-d-flex cu-justify-content-end cu-mt-2'>
            <CancelButton
              className='cu-button-flat'
              onClick={closeCatalogSelectionModal}
            >
              Cancel
            </CancelButton>
            <RequiredPermissionsButton
              permissionName='integrations:add_ecommerce_integration_catalog'
              onClick={enableEcommerceFeatures}
            >
              Let's go!
            </RequiredPermissionsButton>
          </div>
        </ModalFooter>
      </Modal>
      {/* TODO: Move this to be attached to a React top-level page in the future, for example if we have Catalog
        page built in React*/}
      <GoogleCatalogIngestionModal key='googleCatalogIngestionModal' />
    </>
  );
};

const CancelButton = styled(Button)`
  && {
    margin-right: ${({ theme }) => theme.spacings['2']};
  }
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CatalogSelectionModal);
